import { useCallback } from 'react';
import { Concept } from 'api/grpc/resources';
import { cx } from '@linaria/core';
import { uniqWith } from 'rambda';
import { GRPCClient } from 'api/grpc/client';
import { mapOr } from 'utils/fp';
import { createConcept } from 'api-proxy/public/createConcept';
import { ConceptAutocomplete } from './ConceptAutocomplete';
import { ConceptTags } from './ConceptTags';
import { selectorDiv, tagsSectionCls } from './styles';

interface ConceptSelectorProps {
  userOrOrgId: string;
  appId: string;
  selectedConcepts: Concept[];
  labeledConcepts: string[];
  onChange: (concept: Concept[]) => void;
  client: GRPCClient;
  className?: string;
  isDisabled?: boolean;
  wrapperClassName?: string;
  showSelectAll?: boolean;
}

export const ConceptSelector: React.FC<ConceptSelectorProps> = ({
  userOrOrgId,
  appId,
  selectedConcepts,
  labeledConcepts,
  client,
  onChange,
  isDisabled,
  showSelectAll = true,
  ...rest
}): JSX.Element => {
  const handleConceptRemoved = useCallback(
    (concept: Concept) => {
      onChange(selectedConcepts.filter((c) => c.getId() !== concept.getId()));
    },
    [selectedConcepts],
  );

  const handleConceptSelected = useCallback(
    (concepts: Concept[]) => {
      onChange(uniqWith(compareObjects, concepts));
    },
    [selectedConcepts],
  );

  const addNewConcept = async (conceptName: string): Promise<void> => {
    const call = {
      userOrOrgId,
      appId,
      page: 1,
      perPage: 20,
      name: conceptName,
      includeMainAppConcepts: false,
    };
    const result = await client.postConceptsSearches(call);
    const response = mapOr(result, (resp) => resp.getConceptsList(), []);
    const newConcept = response.find((concept) => concept.getName() === conceptName);
    if (newConcept) {
      handleConceptSelected([...selectedConcepts, newConcept]);
    }
  };

  const handleCreateNewConcept = useCallback(
    async (newConcept: string) => {
      const response = await createConcept({
        request: {
          userOrOrgId,
          appId,
        },
        concept: {
          name: newConcept,
          app_id: appId,
        },
      });
      if (response && response.concept && response.concept?.id && response.concept?.name) {
        addNewConcept(newConcept);
      }
    },
    [userOrOrgId, appId, selectedConcepts, addNewConcept],
  );

  const compareObjects = (a: Concept, b: Concept): boolean => a.getId() === b.getId();

  return (
    <div className={selectorDiv}>
      <ConceptAutocomplete
        userOrOrgId={userOrOrgId}
        appId={appId}
        selectedConcepts={selectedConcepts}
        labeledConcepts={labeledConcepts}
        onConceptSelected={handleConceptSelected}
        onConceptCreated={handleCreateNewConcept}
        isDisabled={isDisabled}
        className={rest.wrapperClassName}
        showSelectAll={showSelectAll}
      />
      <div className={cx(tagsSectionCls, rest.className)}>
        {selectedConcepts.map((concept) => (
          <ConceptTags key={concept.getId()} concept={concept} onRemoveTag={() => handleConceptRemoved(concept)} />
        ))}
      </div>
    </div>
  );
};
